/**
 * Sélecteur de point relais — tiroir de panier et page /cart.
 *
 * ⚠️ **Pourquoi un asset et pas un script dans la section.** Le tiroir remplace
 * le `innerHTML` de `#CartDrawer` à chaque modification du panier
 * (`cart-drawer.js`), et **un `<script>` inséré par `innerHTML` ne s'exécute
 * jamais** : un sélecteur autonome serait mort dès le premier changement de
 * quantité. Ce fichier est chargé une fois, délègue ses événements depuis
 * `document`, et se réamorce sur chaque re-rendu.
 *
 * ⚠️ **Les libellés ne peuvent pas venir d'ici** : un asset est servi tel quel,
 * sans passer par Liquid, donc sans filtre `t`. Ils voyagent dans un bloc
 * `<script type="application/json">` du balisage, re-rendu avec lui.
 */
(function () {
  if (window.__nmRelayPicker) return;
  window.__nmRelayPicker = true;

  /**
   * Ce que le re-rendu du tiroir efface et qu'il faut savoir restituer : le
   * panneau était-il ouvert, sur quelle recherche, et avec quels résultats.
   * Le cache évite de rejouer Colissimo à chaque changement de quantité.
   */
  var CACHE = {};
  var LAST = null;
  var OPEN = false;
  /** La fenêtre modale en cours, quand le sélecteur s'ouvre depuis le tiroir. */
  var MODAL = null;

  var CHEVRON =
    '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" ' +
    'stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="m6 9 6 6 6-6"></path></svg>';

  // Les clés du back, dans l'ordre d'affichage. ⚠️ `getDay()` compte à partir du
  // dimanche, la semaine se lit à partir du lundi.
  var API_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  var uid = 0;

  // ─────────────────────────────────────────────────────────────── outillage

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function texts(root) {
    if (root.__nmTexts) return root.__nmTexts;
    var node = root.querySelector('[data-nm-relay-i18n]');
    try {
      root.__nmTexts = JSON.parse(node.textContent);
    } catch (e) {
      root.__nmTexts = { days: [] };
    }
    return root.__nmTexts;
  }

  /**
   * Un morceau du bloc, où qu'il soit.
   *
   * ⚠️ Dans le tiroir, le panneau est DÉPLACÉ hors de sa racine pour vivre dans
   * la fenêtre modale : `root.querySelector` ne le trouve alors plus. La racine
   * garde donc un lien direct vers son panneau, posé à l'amorçage.
   */
  function part(root, name) {
    var selector = '[data-nm-relay-' + name + ']';
    var found = root.querySelector(selector);
    if (found) return found;
    var panel = root.__nmPanel;
    if (!panel) return null;
    return panel.matches(selector) ? panel : panel.querySelector(selector);
  }

  /**
   * La racine à laquelle appartient un élément.
   *
   * ⚠️ Le panneau déplacé dans la fenêtre modale n'est PLUS un descendant de sa
   * racine : `closest('[data-nm-relay]')` y rend `null`. Tout ce qui se trouve
   * dans la fenêtre appartient, par construction, à la racine qui l'a ouverte.
   */
  function rootFor(node) {
    var root = node.closest('[data-nm-relay]');
    if (root) return root;
    if (MODAL && MODAL.shell.contains(node)) return MODAL.root;
    return null;
  }

  function fields(root) {
    return {
      street: part(root, 'street'),
      zip: part(root, 'zip'),
      city: part(root, 'city'),
      country: part(root, 'country'),
      network: part(root, 'network')
    };
  }

  /** Le client choisit-il son réseau dans ce pays ? (zone Shopify « Belgique ») */
  function hasChoice(root, country) {
    return (root.dataset.choice || '').split(',').indexOf(country) !== -1;
  }

  /**
   * Le réseau à demander au serveur, ou `''` là où le pays l'impose.
   *
   * ⚠️ **Rien n'est envoyé hors des pays à choix** : le serveur y applique le
   * réseau du pays (Colissimo en France, en Allemagne, en Autriche). Envoyer
   * « Mondial Relay » pour la France ferait retenir un point que le tarif
   * Colissimo écarterait à l'import.
   */
  function networkOf(root) {
    if (!hasChoice(root, countryOf(root))) return '';
    var group = part(root, 'network');
    var checked = group && group.querySelector('input:checked');
    return (checked && checked.value) || root.dataset.network || 'COLISSIMO';
  }

  /** Montre le choix du réseau là où il existe, le masque ailleurs. */
  function syncNetwork(root) {
    var group = part(root, 'network');
    if (group) group.hidden = !hasChoice(root, countryOf(root));
  }

  /**
   * Le pays retenu pour la recherche.
   *
   * ⚠️ **C'est lui qui décide des RÉSEAUX offerts** — Colissimo seul en France,
   * en Allemagne et en Autriche ; Colissimo ou Mondial Relay au choix en
   * Belgique, au Luxembourg, aux Pays-Bas et en Espagne (`networkOf`). Le
   * serveur valide la correspondance. Tant que le champ n'existait pas, un
   * client belge cherchait dans le référentiel français et n'obtenait rien.
   */
  function countryOf(root) {
    var select = part(root, 'country');
    return (select && select.value) || root.dataset.country || 'FR';
  }

  /** « 450 m » sous le kilomètre, « 1,2 km » au-delà — le séparateur suit la langue. */
  function distanceLabel(metres, lang) {
    if (!metres) return '';
    if (metres < 1000) return Math.round(metres) + ' m';
    var km = metres / 1000;
    try {
      return new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(km) + ' km';
    } catch (e) {
      return km.toFixed(1) + ' km';
    }
  }

  // ────────────────────────────────────────────────────────────── affichage

  /**
   * Les horaires, quand Colissimo les donne.
   *
   * ⚠️ Colissimo remplit les créneaux inutilisés avec « 00:00-00:00 » : les
   * afficher ferait passer un jour de fermeture pour une ouverture nocturne.
   */
  function hoursPanel(point, id, t) {
    var wrap = el('div', 'nm-relay__hours');
    wrap.id = id;
    wrap.hidden = true;
    wrap.appendChild(el('p', 'nm-relay__hours-title', t.hours));

    var hours = point.openingHours || {};
    var today = (new Date().getDay() + 6) % 7;
    var dl = el('dl', 'nm-relay__days');
    var open = 0;

    API_DAYS.forEach(function (key, index) {
      var slots = String(hours[key] || '')
        .split(' ')
        .filter(function (slot) {
          return slot && slot !== '00:00-00:00';
        })
        .join(' · ');
      if (slots) open += 1;

      var row = el(
        'div',
        'nm-relay__day' + (index === today ? ' is-today' : '') + (slots ? '' : ' is-closed')
      );
      row.appendChild(el('dt', null, (t.days || [])[index] || key));
      row.appendChild(el('dd', null, slots || t.closed));
      dl.appendChild(row);
    });

    // Aucun créneau sur la semaine entière : le point n'a pas déclaré ses
    // horaires, ce n'est pas un commerce fermé sept jours sur sept.
    if (open === 0) wrap.appendChild(el('p', 'nm-relay__nohours', t.noHours));
    else wrap.appendChild(dl);
    return wrap;
  }

  function card(root, point, index) {
    var t = texts(root);
    var chosen = point.id === root.dataset.selected;
    var hoursId = 'nm-relay-hours-' + (uid += 1);

    var li = el('li', 'nm-relay__item');
    var box = el('div', 'nm-relay__card' + (chosen ? ' is-chosen' : ''));

    var toggle = el('button', 'nm-relay__toggle');
    toggle.type = 'button';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', hoursId);
    toggle.appendChild(el('span', 'nm-relay__rank', String(index + 1)));

    var info = el('span', 'nm-relay__info');
    info.appendChild(el('span', 'nm-relay__name', point.name));
    // Le point que le client a déjà choisi lui-même (commandes de son compte) :
    // on le signale sans le retenir d'office — c'est le réseau par défaut qui
    // porte l'habitude, le point reste à confirmer d'un clic.
    if (root.dataset.last && root.dataset.last === (point.carrier || 'COLISSIMO') + ':' + point.id) {
      info.appendChild(el('span', 'nm-relay__usual', t.usual));
    }
    info.appendChild(
      el(
        'span',
        'nm-relay__addr',
        [point.address, point.addressComplement].filter(Boolean).join(' ') +
          ' · ' +
          (point.zipCode || '') +
          ' ' +
          (point.city || '')
      )
    );
    var more = el('span', 'nm-relay__more');
    more.appendChild(document.createTextNode(t.showHours));
    more.insertAdjacentHTML('beforeend', CHEVRON);
    info.appendChild(more);
    toggle.appendChild(info);

    var distance = distanceLabel(point.distance, root.dataset.lang || 'fr');
    if (distance) toggle.appendChild(el('span', 'nm-relay__dist', distance));

    var pick = el('button', 'nm-relay__pick', chosen ? t.picked : t.pick);
    pick.type = 'button';
    // Vingt boutons « Choisir » identiques ne disent rien à un lecteur d'écran :
    // le nom du commerce fait la différence.
    pick.setAttribute('aria-label', (chosen ? t.picked : t.pick) + ' — ' + point.name);
    pick.disabled = chosen;
    pick.addEventListener('click', function () {
      choose(root, point);
    });

    var hours = hoursPanel(point, hoursId, t);
    toggle.addEventListener('click', function () {
      var expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      hours.hidden = expanded;
      if (!expanded) box.scrollIntoView({ block: 'nearest' });
    });

    box.appendChild(toggle);
    box.appendChild(pick);
    box.appendChild(hours);
    li.appendChild(box);
    return li;
  }

  function paint(root, points) {
    var list = part(root, 'list');
    var status = part(root, 'status');
    var t = texts(root);
    list.innerHTML = '';
    if (!points.length) {
      status.textContent = t.none;
      return;
    }
    status.textContent = '';
    points.slice(0, 20).forEach(function (point, index) {
      list.appendChild(card(root, point, index));
    });
  }

  // ────────────────────────────────────────────────────────────── recherche

  function search(root, silent) {
    var t = texts(root);
    var status = part(root, 'status');
    var f = fields(root);
    var zip = f.zip.value.trim();
    var city = f.city.value.trim();
    var street = f.street.value.trim();
    var country = countryOf(root);
    var network = networkOf(root);

    if (!zip) {
      status.textContent = t.needZip;
      f.zip.focus();
      return;
    }

    // Le pays entre dans la clé : le même code postal ne désigne pas le même
    // endroit — ni le même réseau — d'un pays à l'autre.
    var key = [country, network, zip, city, street].join('|');
    LAST = { zip: zip, city: city, street: street, country: country, network: network, key: key };

    // Le cache sert au re-rendu du tiroir : rejouer Colissimo à chaque
    // changement de quantité serait inutile, et lent sous les yeux du client.
    if (CACHE[key]) {
      paint(root, CACHE[key]);
      return;
    }

    if (!silent) status.textContent = t.searching;
    part(root, 'list').innerHTML = '';

    var url =
      root.dataset.search +
      '?zip=' +
      encodeURIComponent(zip) +
      '&city=' +
      encodeURIComponent(city || zip) +
      '&country=' +
      encodeURIComponent(country) +
      (street ? '&address=' + encodeURIComponent(street) : '') +
      (network ? '&carrier=' + encodeURIComponent(network) : '');

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then(function (data) {
        CACHE[key] = data.points || [];
        paint(root, CACHE[key]);
      })
      .catch(function () {
        status.textContent = t.error;
      });
  }

  // ─────────────────────────────────────────────────────────────────── choix

  /**
   * ⚠️ Les clés sont un contrat avec le back (`relay/relay-attributes.ts`).
   * En changer une rendrait le relais invisible à l'import, sans erreur.
   *
   * ⚠️ **`routes.cart_update_url`, jamais `routes.root_url` + un chemin.** La
   * racine vaut `/` sur la langue principale mais `/en` SANS slash final sur les
   * autres : la concaténation produisait `/encart/update.js`, un 404 silencieux.
   * Le choix se perdait donc pour tout client anglophone — et rien ne le disait,
   * la liste des points s'affichant normalement. L'URL vient du balisage, où
   * Liquid l'a résolue.
   *
   * ⚠️ **Pas de `location.reload()`** : dans le tiroir, recharger le ferme et le
   * client perd le fil. On redemande la section à Shopify et on remplace ce
   * bloc — l'état « point retenu » vient donc du même Liquid que le rendu
   * initial, sans duplication en JavaScript.
   */
  function choose(root, point) {
    var t = texts(root);
    var status = part(root, 'status');
    status.textContent = t.saving;

    fetch(root.dataset.cartUpdate, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          nm_relay_id: point.id,
          nm_relay_carrier: point.carrier || 'COLISSIMO',
          nm_relay_name: point.name,
          nm_relay_address1: point.address || '',
          nm_relay_address2: point.addressComplement || '',
          nm_relay_zip: point.zipCode || '',
          nm_relay_city: point.city || '',
          nm_relay_country: point.countryCode || 'FR'
        },
        sections: root.dataset.section
      })
    })
      .then(function (r) {
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then(function (data) {
        var html = data.sections && data.sections[root.dataset.section];
        if (!html) {
          // La section n'a pas été rendue : plutôt que de mentir sur l'état,
          // on marque le choix sur place et on laisse le client continuer.
          root.dataset.selected = point.id;
          if (LAST && CACHE[LAST.key]) paint(root, CACHE[LAST.key]);
          status.textContent = '';
          return;
        }
        // Le choix est fait : la fenêtre se referme pour découvrir le tiroir et
        // son bloc « point retenu ». La rouvrir sur une liste devenue inutile
        // obligerait le client à la fermer lui-même.
        OPEN = false;
        closeModal(false);
        replace(root, html);
      })
      .catch(function () {
        status.textContent = t.error;
      });
  }

  /** Remplace ce bloc par sa version fraîchement rendue, puis le réamorce. */
  function replace(root, html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-nm-relay]');
    if (!next || !root.parentNode) return;
    root.parentNode.replaceChild(next, root);
    hydrate(next);
  }

  // ──────────────────────────────────────────────── ouverture et fermeture

  /** Le tiroir : trop étroit pour un dépliant, et il ne défile pas. */
  function isCompact(root) {
    return root.classList.contains('nm-relay--compact');
  }

  var CROSS =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
    'stroke-width="2.5" stroke-linecap="round" aria-hidden="true">' +
    '<path d="M6 6l12 12M18 6L6 18"></path></svg>';

  /**
   * Ouvre le panneau dans une fenêtre posée sur `document.body`.
   *
   * ⚠️ **Le panneau est déplacé, pas recopié.** C'est le même DOM, donc les
   * mêmes champs et la même liste : rien à resynchroniser, et le balisage reste
   * écrit une seule fois, en Liquid. Il retourne à sa place à la fermeture.
   */
  function openModal(root) {
    if (MODAL) closeModal(true);
    var t = texts(root);
    var panel = root.__nmPanel;
    if (!panel) return;

    var shell = el('div', 'nm-relay-modal');
    shell.setAttribute('role', 'dialog');
    shell.setAttribute('aria-modal', 'true');
    shell.setAttribute('aria-label', t.modalTitle || '');

    var backdrop = el('button', 'nm-relay-modal__backdrop');
    backdrop.type = 'button';
    backdrop.setAttribute('aria-label', t.close || '');
    backdrop.addEventListener('click', function () {
      OPEN = false;
      closeModal(true);
    });

    var box = el('div', 'nm-relay-modal__box');
    var head = el('div', 'nm-relay-modal__head');
    head.appendChild(el('p', 'nm-relay-modal__title', t.modalTitle));

    var close = el('button', 'nm-relay-modal__close');
    close.type = 'button';
    close.setAttribute('aria-label', t.close || '');
    close.innerHTML = CROSS;
    close.addEventListener('click', function () {
      OPEN = false;
      closeModal(true);
    });
    head.appendChild(close);

    var body = el('div', 'nm-relay-modal__body');
    panel.hidden = false;
    body.appendChild(panel);

    box.appendChild(head);
    box.appendChild(body);
    shell.appendChild(backdrop);
    shell.appendChild(box);
    document.body.appendChild(shell);

    MODAL = { shell: shell, root: root, panel: panel, opener: document.activeElement };

    var zip = part(root, 'zip');
    if (zip) zip.focus({ preventScroll: true });
  }

  /**
   * Referme la fenêtre. `restore` remet le panneau dans sa racine ; on ne le
   * remet pas quand la racine a été remplacée entre-temps par un re-rendu du
   * tiroir — la nouvelle en porte déjà un, tout neuf.
   */
  function closeModal(restore) {
    if (!MODAL) return;
    var m = MODAL;
    MODAL = null;
    if (restore && m.root.isConnected) {
      m.panel.hidden = true;
      m.root.appendChild(m.panel);
    }
    m.shell.remove();
    if (restore && m.opener && m.opener.isConnected) m.opener.focus({ preventScroll: true });
  }

  function openPanel(root) {
    OPEN = true;
    if (isCompact(root)) openModal(root);
    else part(root, 'panel').hidden = false;
  }

  function closePanel(root) {
    OPEN = false;
    if (MODAL) closeModal(true);
    else part(root, 'panel').hidden = true;
  }

  function isOpen(root) {
    return isCompact(root) ? !!MODAL : !part(root, 'panel').hidden;
  }

  // ────────────────────────────────────────────────────────────── amorçage

  /**
   * Rend un bloc opérant, qu'il vienne du chargement de la page ou d'un
   * re-rendu du tiroir. Idempotent : un bloc déjà amorcé est laissé tel quel.
   */
  function hydrate(root) {
    // ⚠️ **Un drapeau en propriété JavaScript, jamais en attribut `data-`.** Un
    // attribut survit à la sérialisation du HTML : si quoi que ce soit refait un
    // aller-retour `innerHTML`, le bloc reviendrait marqué « déjà amorcé » avec
    // des cartes sans aucun écouteur — des boutons morts, et rien à l'écran pour
    // le dire. Une propriété disparaît avec le nœud, donc un nœud reconstruit se
    // réamorce toujours.
    if (root.__nmReady) return;
    root.__nmReady = true;
    root.__nmPanel = root.querySelector('[data-nm-relay-panel]');

    // La racine qui possédait la fenêtre a été remplacée. Deux cas, et le
    // second n'est pas théorique : si le tiroir se réécrit pendant que le
    // panneau vit dans la fenêtre, la nouvelle racine arrive SANS panneau.
    if (MODAL && !MODAL.root.isConnected) {
      if (root.__nmPanel) {
        // La nouvelle racine a son propre panneau, tout neuf : la fenêtre
        // repart de celui-là, l'ancien s'en va avec sa coquille.
        closeModal(false);
      } else {
        // Personne d'autre ne détient de panneau : la fenêtre vivante est
        // rattachée à la nouvelle racine. La détruire perdrait la recherche en
        // cours et laisserait un sélecteur incapable de se rouvrir.
        MODAL.root = root;
        root.__nmPanel = MODAL.panel;
      }
    }

    var f = fields(root);
    f.street.value = (LAST && LAST.street) || root.dataset.street || '';
    f.zip.value = (LAST && LAST.zip) || root.dataset.zip || '';
    f.city.value = (LAST && LAST.city) || root.dataset.city || '';
    // Le tiroir se re-rend à chaque changement de quantité : sans cette reprise,
    // le pays choisi retombait sur celui du compte au premier ajout d'article.
    if (f.country) f.country.value = (LAST && LAST.country) || root.dataset.country || 'FR';
    // Même reprise pour le réseau choisi. Sans recherche en cours, c'est le
    // réseau par défaut rendu par Liquid (déjà retenu, sinon habituel, sinon
    // Colissimo) qui reste coché.
    if (f.network && LAST && LAST.network) {
      Array.prototype.forEach.call(f.network.querySelectorAll('input'), function (input) {
        input.checked = input.value === LAST.network;
      });
    }
    syncNetwork(root);

    if (!OPEN) return;
    // Une fenêtre déjà rattachée à cette racine est déjà ouverte : la rouvrir
    // la ferait clignoter et reprendrait le focus au client.
    if (!MODAL || MODAL.root !== root) openPanel(root);
    if (LAST && CACHE[LAST.key]) paint(root, CACHE[LAST.key]);
    else if (f.zip.value) search(root, true);
  }

  function hydrateAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nm-relay]'), hydrate);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var opener = target.closest('[data-nm-relay-open]');
    if (opener) {
      var root = opener.closest('[data-nm-relay]');
      if (!root) return;
      if (isOpen(root)) {
        closePanel(root);
        return;
      }
      openPanel(root);
      var f = fields(root);
      if (!part(root, 'list').children.length && f.zip.value) search(root);
      return;
    }

    var searcher = target.closest('[data-nm-relay-search]');
    if (searcher) {
      var searchRoot = rootFor(searcher);
      if (searchRoot) search(searchRoot);
    }
  });

  /**
   * Changement de pays ou de réseau : on relance aussitôt.
   *
   * Le pays change le RÉSEAU interrogé, donc la liste entière : la laisser
   * affichée sous un autre pays montrerait des points où le colis n'irait pas.
   * Délégué depuis `document`, comme le reste — le tiroir remplace son contenu
   * et un écouteur posé sur le champ disparaîtrait avec lui.
   */
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('[data-nm-relay-country]') && !target.closest('[data-nm-relay-network]')) return;
    var root = rootFor(target);
    if (!root) return;
    // Le réseau ne se montre que dans les pays où le client a le choix.
    syncNetwork(root);
    var f = fields(root);
    if (f.zip.value.trim()) search(root);
  });

  /**
   * Échap.
   *
   * ⚠️ **En phase de CAPTURE, et sur les DEUX événements.** Dawn ferme le tiroir
   * sur `keyup` (cart-drawer.js, ligne 5), pas sur `keydown`. Fermer la fenêtre
   * au `keydown` rend le focus au bouton — qui vit DANS le tiroir — et le `keyup`
   * qui suit y remontait aussitôt : une seule touche fermait la fenêtre PUIS le
   * tiroir derrière elle. Le `keyup` est donc avalé lui aussi.
   *
   * Trouvé sur le thème réel : le harnais local n'a pas le tiroir de Dawn, et
   * laissait passer.
   */
  var swallowEscapeUp = false;

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape' || !MODAL) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      swallowEscapeUp = true;
      OPEN = false;
      closeModal(true);
    },
    true,
  );

  document.addEventListener(
    'keyup',
    function (event) {
      if (event.key !== 'Escape' || !swallowEscapeUp) return;
      swallowEscapeUp = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true,
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var input = event.target;
    if (!input || !input.matches) return;
    if (!input.matches('[data-nm-relay-zip], [data-nm-relay-city], [data-nm-relay-street]')) return;
    event.preventDefault();
    var root = rootFor(input);
    if (root) search(root);
  });

  // Le tiroir se réécrit sans prévenir : on observe plutôt que de deviner.
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(hydrateAll).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      hydrateAll();
      watchDrawer();
    });
  } else {
    hydrateAll();
    watchDrawer();
  }
})();
